import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api/client';
import Icon from '../../components/AppIcon';

const MyTroncReport = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchMyTroncData();
}, [selectedMonth]);

  const fetchMyTroncData = async () => {
  setIsLoading(true);
    setError(null);

    try {
 const [year, month] = selectedMonth.split('-').map(Number);
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0); // Last day of month

      const response = await apiClient.get('/employees/me/tronc-report', {
        fromDate: startDate.toISOString(),
        toDate: endDate.toISOString()
   });

      setReportData(response?.data);
    } catch (err) {
      console.error('Failed to fetch TRONC report:', err);
      setError(err.message || 'Failed to load your TRONC report');
    } finally {
  setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const formatFullDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Generate month options for last 12 months
  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  }, []);

  // Export to CSV
  const handleExport = async () => {
    if (!reportData?.allocations?.length) return;

    setIsExporting(true);
    try {
      const monthLabel = monthOptions.find(m => m.value === selectedMonth)?.label || selectedMonth;
      const headers = ['Date', 'Hours Worked', 'Pool Total', 'Your Share', 'Status', 'Rule Applied'];
      const rows = reportData.allocations.map(a => [
        formatFullDate(a.date),
        a.hoursWorked?.toFixed(1) || '0.0',
        formatCurrency(a.poolTotal),
        formatCurrency(a.amount),
        a.isDistributed ? 'Distributed' : 'Pending',
        a.ruleName || '-'
      ]);

      // Add summary row
      rows.push([]);
      rows.push(['SUMMARY', '', '', '', '', '']);
      rows.push(['Total Hours', reportData.totalHours?.toFixed(1) || '0.0', '', '', '', '']);
      rows.push(['Total Earnings', '', '', formatCurrency(reportData.totalAmount), '', '']);

      const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
   const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
    a.href = url;
      a.download = `my-tronc-${selectedMonth}.csv`;
    a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  // Download policy document
  const handleDownloadPolicy = async () => {
    // This would typically download a policy document from the API
    // For now, generate a simple policy summary
    const policyContent = `
TRONC DISTRIBUTION POLICY
========================

Employee: ${reportData?.employeeName || user?.fullName || 'N/A'}
Location: ${reportData?.locationName || 'All Locations'}
Generated: ${new Date().toLocaleDateString('en-GB')}

CURRENT DISTRIBUTION RULE
-------------------------
Name: ${reportData?.activeRule?.name || 'No active rule'}
Type: ${reportData?.activeRule?.type || 'N/A'}
${reportData?.activeRule?.description ? `Description: ${reportData.activeRule.description}` : ''}
${reportData?.activeRule?.calculation ? `\nCalculation Method:\n${reportData.activeRule.calculation}` : ''}

POLICY OVERVIEW
---------------
- TRONC (Tips, Gratuities, and Service Charges) is distributed fairly among eligible staff.
- Distribution is calculated based on the active rule for each period.
- All distributions are recorded and auditable for HMRC compliance.
- Your share depends on hours worked, job role, and the distribution formula in effect.

For questions about TRONC distributions, please contact your manager.
    `;

    const blob = new Blob([policyContent.trim()], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tronc-policy.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
    <>
<Helmet>
          <title>My TRONC - Rocket Tronc</title>
     </Helmet>
        <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
 <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your TRONC report...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>My TRONC - Rocket Tronc</title>
      </Helmet>

      <div className="max-w-5xl mx-auto p-6 space-y-6">
   {/* Header */}
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
         <h1 className="text-2xl font-bold">My TRONC</h1>
      <p className="text-muted-foreground">
          View your tip distributions and current policy
          </p>
          </div>

   <div className="flex items-center gap-3">
            <select
        value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
  className="input w-auto"
            >
    {monthOptions.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
       ))}
       </select>
          </div>
     </div>

        {error ? (
       <div className="card p-8 text-center">
            <Icon name="AlertTriangle" size={48} className="text-warning mx-auto mb-4" />
   <h3 className="font-semibold mb-2">Unable to Load Report</h3>
 <p className="text-muted-foreground mb-4">{error}</p>
          <button onClick={fetchMyTroncData} className="btn btn-primary">
     <Icon name="RefreshCw" size={18} />
              Try Again
   </button>
   </div>
        ) : (
          <>
            {/* Summary Cards */}
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-3">
<div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
          <Icon name="PoundSterling" size={20} className="text-success" />
            </div>
         <span className="text-sm text-muted-foreground">Total Earned</span>
         </div>
      <p className="text-3xl font-bold text-success">
           {formatCurrency(reportData?.totalAmount || 0)}
 </p>
      <p className="text-sm text-muted-foreground mt-1">
        {reportData?.allocations?.length || 0} distribution{reportData?.allocations?.length !== 1 ? 's' : ''} this month
        </p>
    </div>

   <div className="card p-6">
                <div className="flex items-center gap-3 mb-3">
           <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon name="Clock" size={20} className="text-primary" />
          </div>
         <span className="text-sm text-muted-foreground">Hours Worked</span>
     </div>
    <p className="text-3xl font-bold">
               {reportData?.totalHours?.toFixed(1) || '0.0'}
        </p>
      <p className="text-sm text-muted-foreground mt-1">
      This month
   </p>
              </div>

      <div className="card p-6">
     <div className="flex items-center gap-3 mb-3">
    <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
  <Icon name="TrendingUp" size={20} className="text-warning" />
     </div>
                  <span className="text-sm text-muted-foreground">Avg per Hour</span>
   </div>
      <p className="text-3xl font-bold">
      {formatCurrency(reportData?.totalHours > 0 ? reportData.totalAmount / reportData.totalHours : 0)}
   </p>
     <p className="text-sm text-muted-foreground mt-1">
     TRONC rate
         </p>
   </div>
   </div>

{/* Active Distribution Rule / Policy Section */}
   <div className="card overflow-hidden">
         <div className="p-4 border-b border-border bg-gradient-to-r from-primary/5 to-primary/10">
       <div className="flex items-center justify-between">
    <div className="flex items-center gap-3">
             <Icon name="FileText" size={20} className="text-primary" />
            <h3 className="font-semibold">Current TRONC Policy</h3>
       </div>
        <button 
         onClick={handleDownloadPolicy}
             className="btn btn-sm btn-outline"
              >
   <Icon name="Download" size={16} />
        Download Policy
      </button>
            </div>
      </div>
   
     <div className="p-6">
     {reportData?.activeRule ? (
              <div className="space-y-4">
           <div className="flex items-center gap-3">
    <span className="badge badge-primary">{reportData.activeRule.type}</span>
        <span className="font-semibold text-lg">{reportData.activeRule.name}</span>
     </div>

               {reportData.activeRule.description && (
    <p className="text-muted-foreground">{reportData.activeRule.description}</p>
         )}
     
  {reportData.activeRule.calculation && (
   <div className="bg-muted/50 rounded-lg p-4">
 <p className="text-xs text-muted-foreground font-medium mb-2">HOW YOUR SHARE IS CALCULATED</p>
   <p className="text-sm">{reportData.activeRule.calculation}</p>
          </div>
     )}
              </div>
   ) : (
            <div className="text-center py-4">
         <Icon name="AlertCircle" size={24} className="text-muted-foreground mx-auto mb-2" />
   <p className="text-muted-foreground">No active distribution rule configured.</p>
   <p className="text-sm text-muted-foreground">Please contact your manager.</p>
        </div>
    )}
      </div>
    </div>

     {/* Daily Breakdown */}
          {!reportData?.allocations?.length ? (
              <div className="card p-8 text-center">
    <Icon name="PieChart" size={48} className="text-muted-foreground mx-auto mb-4" />
    <h3 className="font-semibold mb-2">No Distributions Yet</h3>
       <p className="text-muted-foreground">
You don't have any TRONC distributions for {monthOptions.find(m => m.value === selectedMonth)?.label}.
    </p>
        </div>
         ) : (
          <div className="card">
 <div className="p-4 border-b border-border flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <Icon name="Calendar" size={18} />
    Daily Breakdown
       </h3>
       <button
 onClick={handleExport}
    disabled={isExporting}
    className="btn btn-sm btn-outline"
       >
     {isExporting ? (
         <>
     <Icon name="Loader2" size={16} className="animate-spin" />
        Exporting...
  </>
  ) : (
     <>
  <Icon name="Download" size={16} />
          Export CSV
           </>
         )}
    </button>
                </div>
          <div className="overflow-x-auto">
        <table className="w-full">
      <thead className="bg-muted/50">
         <tr>
 <th className="text-left p-4 text-sm font-medium">Date</th>
        <th className="text-right p-4 text-sm font-medium">Hours</th>
 <th className="text-right p-4 text-sm font-medium">Pool Total</th>
     <th className="text-right p-4 text-sm font-medium">Your Share</th>
  <th className="text-center p-4 text-sm font-medium">Status</th>
               </tr>
          </thead>
         <tbody className="divide-y divide-border">
       {reportData.allocations.map((allocation) => (
   <tr key={allocation.id} className="hover:bg-muted/30">
      <td className="p-4">
           <span className="font-medium">{formatDate(allocation.date)}</span>
       </td>
        <td className="p-4 text-right">
             {allocation.hoursWorked?.toFixed(1) || '-'}
         </td>
          <td className="p-4 text-right text-muted-foreground">
          {formatCurrency(allocation.poolTotal)}
     </td>
                <td className="p-4 text-right font-semibold text-success">
   {formatCurrency(allocation.amount)}
</td>
            <td className="p-4 text-center">
     {allocation.isDistributed ? (
   <span className="badge badge-success">
     <Icon name="Check" size={12} className="mr-1" />
          Distributed
          </span>
            ) : (
        <span className="badge badge-warning">
      <Icon name="Clock" size={12} className="mr-1" />
           Pending
       </span>
      )}
     </td>
        </tr>
   ))}
          </tbody>
  <tfoot className="bg-muted/30 font-semibold">
         <tr>
          <td className="p-4">Total</td>
         <td className="p-4 text-right">{reportData.totalHours?.toFixed(1) || '0.0'}</td>
             <td className="p-4 text-right">{formatCurrency(reportData.poolTotal)}</td>
          <td className="p-4 text-right text-success">{formatCurrency(reportData.totalAmount)}</td>
     <td className="p-4"></td>
             </tr>
           </tfoot>
           </table>
       </div>
              </div>
            )}

          {/* Historical Rules - Show only if there are any */}
            {reportData?.historicalRules?.length > 0 && (
<div className="card">
            <div className="p-4 border-b border-border">
           <h3 className="font-semibold flex items-center gap-2">
       <Icon name="History" size={18} />
         Previous Distribution Rules
           </h3>
         </div>
   <div className="divide-y divide-border">
{reportData.historicalRules.map((rule, index) => (
        <div key={index} className="p-4">
<div className="flex items-center justify-between">
  <div>
   <p className="font-medium">{rule.name}</p>
        <p className="text-sm text-muted-foreground">
           {formatFullDate(rule.effectiveFrom)} - {formatFullDate(rule.effectiveTo)}
          </p>
   </div>
<span className="badge badge-outline">{rule.type}</span>
     </div>
          </div>
           ))}
                </div>
  </div>
     )}

     {/* Help Section */}
            <div className="card p-6 bg-muted/30">
   <div className="flex items-start gap-4">
     <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
     <Icon name="HelpCircle" size={20} className="text-primary" />
     </div>
    <div>
        <h4 className="font-semibold mb-2">Need Help?</h4>
       <p className="text-sm text-muted-foreground">
       If you have questions about your TRONC distributions or believe there's an error in your allocations,
                 please contact your manager or the payroll department.
          </p>
                </div>
        </div>
   </div>
 </>
        )}
      </div>
    </>
  );
};

export default MyTroncReport;
